import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Phone, Calendar, User, Share2, Facebook, Linkedin, LinkIcon, HelpCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import OptimizedImage from '../../components/OptimizedImage';
import SEO from '../../components/SEO';
import { toast } from 'sonner';

const SLUG = 'prix-debarras-maison-annecy';
const PUBLISH_DATE = '2026-04-10';

const BlogPrixDebarras = () => {
  const faq = [
    {
      question: 'Un débarras de maison peut-il vraiment être gratuit à Annecy ?',
      answer: 'Oui, dans certains cas. Si le contenu de la maison comprend suffisamment d\'objets valorisables (mobilier ancien en bon état, électroménager récent, métaux, outils), leur revente ou recyclage peut couvrir l\'intégralité du coût de l\'intervention. Lors de la visite, nous évaluons ce potentiel et l\'intégrons au devis. Les maisons du secteur Annecy-le-Vieux ou des bords du lac présentent souvent ce profil.'
    },
    {
      question: 'Le prix change-t-il selon le quartier d\'Annecy ?',
      answer: 'Le quartier n\'est pas un critère tarifaire en soi, mais l\'accessibilité du bien peut faire varier le devis de 10 à 20 %. Un appartement au 4e sans ascenseur dans la Vieille Ville coûtera plus cher qu\'un pavillon de plain-pied à Seynod, à volume équivalent, en raison du temps de portage supplémentaire.'
    },
    {
      question: 'Peut-on négocier le prix d\'un débarras ?',
      answer: 'Le devis est ferme et établi après visite. En revanche, vous pouvez réduire le coût en préparant le terrain : vider les tiroirs et placards, trier les objets personnels à conserver, et dégager les accès. Moins notre équipe a de tri à faire sur place, plus l\'intervention est rapide — et moins elle coûte.'
    },
    {
      question: 'Y a-t-il des frais cachés après le devis ?',
      answer: 'Non. Le montant annoncé lors de la visite est le montant facturé, même si le volume réel dépasse légèrement l\'estimation. Nous ne facturons ni le déplacement pour le devis, ni les frais de déchetterie, ni le nettoyage de fin d\'intervention. Tout est inclus.'
    }
  ];

  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Combien coûte un débarras de maison à Annecy en 2026 ?",
    "description": "Tarifs réels, fourchettes de prix par type de bien, facteurs qui influencent le coût et cas du débarras gratuit. Guide complet pour Annecy et le bassin annécien.",
    "image": `https://debeco.fr/blog/${SLUG}/hero.jpg`,
    "datePublished": PUBLISH_DATE,
    "dateModified": PUBLISH_DATE,
    "author": { "@type": "Organization", "name": "DEBECO" },
    "publisher": {
      "@type": "Organization",
      "name": "DEBECO",
      "logo": { "@type": "ImageObject", "url": "https://debeco.fr/images/logo-header.png" }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://debeco.fr/blog/${SLUG}` }
  };

  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://debeco.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://debeco.fr/blog" },
      { "@type": "ListItem", "position": 3, "name": "Prix débarras maison Annecy", "item": `https://debeco.fr/blog/${SLUG}` }
    ]
  };

  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": { "@type": "Answer", "text": q.answer }
    }))
  };

  useEffect(() => {
    const scripts = [jsonLdArticle, jsonLdBreadcrumb, jsonLdFaq].map(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach(s => s.remove());
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(`https://debeco.fr/blog/${SLUG}`);
    toast.success('Lien copié !');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <SEO
        title="Prix débarras maison Annecy 2026 : tarifs réels | DEBECO"
        description="Combien coûte un débarras de maison à Annecy ? De 800 à 4 000 € selon le volume. Fourchettes détaillées, facteurs de prix et cas du débarras gratuit."
        canonical={`/blog/${SLUG}`}
        ogImage={`/blog/${SLUG}/hero.jpg`}
      />

      {/* Breadcrumb */}
      <nav className="bg-white border-b" aria-label="Fil d'Ariane" data-testid="breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <ol className="flex items-center text-sm text-gray-600 flex-wrap gap-y-1">
            <li><Link to="/" className="hover:text-emerald-600">Accueil</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/blog" className="hover:text-emerald-600">Blog</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li className="text-blue-900 font-medium">Prix débarras maison Annecy</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-medium">Guide tarifaire</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-6" data-testid="article-h1">Combien coûte un débarras de maison à Annecy en 2026 ?</h1>
            <div className="flex flex-wrap items-center gap-6 text-blue-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <time dateTime={PUBLISH_DATE}>10 avril 2026</time>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>DEBECO</span>
              </div>
              <span className="text-blue-300">~8 min de lecture</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Hero image */}
            <figure className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <OptimizedImage
                src={`/blog/${SLUG}/hero.jpg`}
                webpSrc={`/blog/${SLUG}/hero.webp`}
                alt="Cartons et meubles empilés dans une cuisine en cours de débarras, estimation du prix à Annecy"
                className="w-full h-96 object-cover"
                width="1200"
                height="1800"
                decoding="async"
                data-testid="article-hero-image"
              />
            </figure>

            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <div className="prose prose-lg max-w-none text-gray-700">

                  {/* Introduction */}
                  <p className="text-xl leading-relaxed mb-6">
                    Vous avez une maison à vider à Annecy ou dans le bassin annécien, et la première question qui vient est logiquement celle du budget. Sauf que les tarifs varient énormément d'un prestataire à l'autre, d'un bien à l'autre, et les devis en ligne « à partir de 99 € » ne reflètent jamais la réalité du terrain.
                  </p>
                  <p className="text-lg leading-relaxed mb-6">
                    Dans cet article, nous posons les chiffres sur la table : fourchettes réelles constatées en 2026 autour d'Annecy, facteurs qui font grimper ou baisser la facture, et les situations où un débarras peut être <strong>partiellement ou totalement gratuit</strong>. L'objectif : que vous puissiez estimer votre budget avant même de décrocher le téléphone.
                  </p>

                  {/* H2 - Facteurs */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Les 5 facteurs qui déterminent le prix d'un débarras</h2>
                  <p className="mb-6">
                    Le tarif d'un vide-maison n'est pas arbitraire. Il se calcule à partir de critères objectifs que tout professionnel sérieux évalue lors d'une visite préalable. Voici les cinq principaux, classés par ordre d'impact sur le devis final.
                  </p>

                  {/* Infographic - HTML */}
                  <div className="my-8 rounded-xl shadow-lg overflow-hidden border" data-testid="infographie-facteurs">
                    <div className="bg-blue-950 px-6 py-4">
                      <h3 className="text-white font-bold text-lg m-0">Les 5 facteurs qui font varier le prix d'un débarras</h3>
                    </div>
                    <div className="divide-y">
                      {[
                        { num: '1', color: 'bg-emerald-500', title: 'Volume à évacuer', desc: 'Facteur principal : nombre de pièces, densité d\'encombrement.', impact: '60 – 70 %' },
                        { num: '2', color: 'bg-blue-500', title: 'Accessibilité du bien', desc: 'Étage, ascenseur, escalier étroit, stationnement.', impact: '10 – 20 %' },
                        { num: '3', color: 'bg-purple-500', title: 'Nature des objets', desc: 'Objets lourds (piano, coffre-fort), matériaux spéciaux.', impact: '5 – 15 %' },
                        { num: '4', color: 'bg-amber-500', title: 'Valorisation possible', desc: 'Meubles revendables, antiquités, métaux : réduction du coût.', impact: '-10 à 100 %' },
                        { num: '5', color: 'bg-red-500', title: 'Délai d\'intervention', desc: 'Urgence < 48h = surcoût, planifié = tarif standard.', impact: '5 – 10 %' }
                      ].map((f) => (
                        <div key={f.num} className="flex items-stretch">
                          <div className={`${f.color} w-2 flex-shrink-0`} />
                          <div className="flex-1 px-5 py-4 flex items-center justify-between gap-4">
                            <div>
                              <p className="font-bold text-blue-900 text-base m-0">{f.num}. {f.title}</p>
                              <p className="text-gray-600 text-sm m-0 mt-1">{f.desc}</p>
                            </div>
                            <span className="bg-blue-100 text-blue-900 font-semibold text-sm px-3 py-1.5 rounded-lg whitespace-nowrap flex-shrink-0">Impact : {f.impact}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gray-50 px-6 py-2.5 text-xs text-gray-500 text-center">
                      Estimations DEBECO basées sur +500 interventions en Savoie et Haute-Savoie
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">1. Le volume à évacuer : le facteur n°1</h3>
                  <p className="mb-4">
                    C'est simple : plus il y a de choses à sortir, plus l'intervention dure longtemps et mobilise de personnel. Un studio meublé sommairement représente 5 à 10 m³, tandis qu'une maison familiale de 5 pièces avec cave, grenier et garage peut atteindre 80 à 120 m³. L'écart de prix entre les deux est proportionnel.
                  </p>
                  <p className="mb-6">
                    À Annecy, les maisons du quartier des Romains ou de La Prairie, souvent habitées depuis plusieurs décennies, présentent des volumes importants. À l'inverse, un appartement récent à Bonlieu, occupé quelques années seulement, sera nettement plus rapide à vider.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">2. L'accessibilité du bien</h3>
                  <p className="mb-6">
                    Un pavillon de plain-pied avec accès direct en camion, c'est le scénario idéal. Mais à Annecy, ce n'est pas toujours la réalité. Les immeubles anciens de la Vieille Ville imposent des cages d'escalier étroites et l'absence d'ascenseur. Les rues piétonnes autour du Thiou compliquent le stationnement. Les maisons sur les hauteurs — côté Parmelan ou Vovray — ont parfois des accès en pente raide. Chaque contrainte allonge le temps d'intervention, et donc le coût.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">3. La nature des objets</h3>
                  <p className="mb-6">
                    Un canapé standard et un piano droit, ce n'est pas le même chantier. Les objets lourds, fragiles ou nécessitant un traitement spécial (réfrigérateur à dégager, matelas à recycler, produits chimiques à évacuer en déchetterie spécialisée) augmentent le temps de manutention. À l'inverse, du mobilier léger et des cartons bien empilés se débarrassent vite.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">4. La valorisation des biens récupérés</h3>
                  <p className="mb-4">
                    C'est le facteur que beaucoup ignorent, et pourtant c'est celui qui peut <strong>faire baisser drastiquement la facture</strong>. Lorsqu'une maison contient des meubles en bon état, de l'électroménager fonctionnel, des objets de brocante ou des métaux (cuivre, laiton, fonte), leur valeur de revente ou de recyclage est déduite du coût total.
                  </p>
                  <p className="mb-6">
                    Dans les propriétés cossues du tour du lac — Veyrier, Menthon-Saint-Bernard, Talloires — ou dans les maisons bourgeoises d'Annecy-le-Vieux, nous trouvons régulièrement du mobilier de qualité, de la vaisselle ancienne ou des outils professionnels qui couvrent une part significative de l'intervention.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">5. Le délai d'intervention</h3>
                  <p className="mb-6">
                    Une intervention planifiée à 10 jours coûte moins cher qu'une urgence à 48 heures. Logique : l'urgence mobilise une équipe en dehors de la planification habituelle. Si votre situation le permet, anticipez la demande de devis. En revanche, si vous êtes pressé — signature de vente imminente, fin de bail, décision judiciaire — nous nous adaptons, et le surcoût reste raisonnable (5 à 10 % du total).
                  </p>

                  {/* H2 - Fourchettes */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Fourchettes de prix constatées à Annecy en 2026</h2>
                  <p className="mb-6">
                    Ces tarifs sont indicatifs et basés sur nos interventions récentes dans le bassin annécien. Le devis définitif est toujours établi après visite gratuite sur place — c'est la seule manière d'évaluer précisément le volume et les contraintes.
                  </p>

                  {/* Price table - HTML */}
                  <div className="my-8 rounded-xl shadow-lg overflow-hidden border" data-testid="tableau-prix">
                    <div className="bg-blue-950 px-6 py-4">
                      <h3 className="text-white font-bold text-lg m-0">Fourchettes de prix indicatives — Débarras à Annecy (2026)</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-blue-50">
                            <th className="text-left px-5 py-3 font-semibold text-blue-900">Type de bien</th>
                            <th className="text-left px-5 py-3 font-semibold text-blue-900">Volume moyen</th>
                            <th className="text-left px-5 py-3 font-semibold text-blue-900">Fourchette de prix</th>
                            <th className="text-left px-5 py-3 font-semibold text-blue-900">Durée estimée</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {[
                            { type: 'Studio / T1', volume: '5 – 15 m³', prix: '200 – 600 €', duree: '2 – 4 h' },
                            { type: 'T2 / T3', volume: '15 – 30 m³', prix: '500 – 1 200 €', duree: '½ journée' },
                            { type: 'T4 / T5', volume: '30 – 50 m³', prix: '1 000 – 2 500 €', duree: '1 journée' },
                            { type: 'Maison 3-4 pièces', volume: '40 – 70 m³', prix: '1 500 – 3 500 €', duree: '1 – 2 jours' },
                            { type: 'Grande maison (5+)', volume: '60 – 120 m³', prix: '2 500 – 5 000 €', duree: '2 – 3 jours' },
                            { type: 'Maison + dépendances', volume: '80 – 150 m³', prix: '3 000 – 6 000 €', duree: '2 – 4 jours' }
                          ].map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-emerald-50/40'}>
                              <td className="px-5 py-3 font-medium text-blue-900">{row.type}</td>
                              <td className="px-5 py-3 text-gray-700">{row.volume}</td>
                              <td className="px-5 py-3 font-semibold text-emerald-700">{row.prix}</td>
                              <td className="px-5 py-3 text-gray-700">{row.duree}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-gray-50 px-6 py-2.5 text-xs text-gray-500 text-center">
                      Prix TTC indicatifs. Le devis définitif est établi après visite gratuite sur place.
                    </div>
                  </div>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-6 rounded-r-lg">
                    <p className="text-amber-900 font-medium mb-1">Bon à savoir</p>
                    <p className="text-amber-800 text-sm">
                      Ces fourchettes concernent un débarras complet (mobilier + contenu des placards + électroménager). Si vous avez déjà trié et vidé une partie des pièces, le coût sera logiquement inférieur. Chaque mètre cube en moins fait baisser la facture.
                    </p>
                  </div>

                  {/* H2 - Débarras gratuit */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Le cas du débarras gratuit : mythe ou réalité ?</h2>
                  <p className="mb-4">
                    Un débarras entièrement gratuit, ça existe — mais pas dans tous les cas. Le principe est simple : si la valeur des objets récupérables (meubles, électroménager, métaux, brocante) dépasse le coût de la main-d'œuvre et du transport, l'intervention ne vous coûte rien. Dans de rares cas, elle peut même vous rapporter un petit complément.
                  </p>
                  <p className="mb-4">
                    <strong>Quand est-ce possible ?</strong> Typiquement dans les maisons bien meublées, avec du mobilier massif en bon état, de l'électroménager récent, des outils, du cuivre, ou des objets de collection. Les maisons familiales des bords du lac d'Annecy — Sevrier, Saint-Jorioz, Duingt — présentent souvent ce profil, surtout quand elles ont été entretenues et meublées avec soin pendant des décennies.
                  </p>
                  <p className="mb-6">
                    <strong>Quand est-ce impossible ?</strong> Lorsque le contenu est essentiellement composé de déchets (vieux matelas, meubles en aggloméré cassés, vêtements usagés en quantité, encombrants divers). Dans ce cas, le coût de traitement en déchetterie s'ajoute au coût de main-d'œuvre, et le débarras a un prix. C'est la situation la plus fréquente dans les caves et greniers laissés à l'abandon.
                  </p>

                  {/* H2 - Spécificités Annecy */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Spécificités annéciennes : ce qui change par rapport à d'autres villes</h2>
                  <p className="mb-4">
                    Annecy n'est pas une ville « standard » pour le débarras. Plusieurs facteurs locaux influencent directement les tarifs et la logistique.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">Un parc immobilier très varié</h3>
                  <p className="mb-6">
                    En quelques kilomètres, on passe d'un immeuble du XVIIe siècle dans la Vieille Ville à une résidence contemporaine à Bonlieu, d'un chalet rénové sur les hauteurs du Parmelan à un pavillon des années 60 à Loverchy. Chaque type de bien a ses contraintes propres. C'est pourquoi la visite préalable est indispensable : un devis par téléphone serait forcément imprécis.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">Le marché immobilier sous pression</h3>
                  <p className="mb-6">
                    La proximité de Genève et l'attractivité touristique du lac maintiennent une tension permanente sur l'immobilier annécien. Les transactions se concluent vite, les délais de libération sont courts. Résultat : beaucoup de nos clients ont besoin d'un <Link to="/debarras-maison-annecy" className="text-emerald-600 hover:text-emerald-700 font-medium">débarras de maison rapide à Annecy</Link> — sous une semaine, parfois moins. Cette pression sur les délais est spécifique à la région.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">Les communes du tour du lac</h3>
                  <p className="mb-6">
                    Veyrier-du-Lac, Menthon-Saint-Bernard, Talloires-Montmin, Sevrier, Saint-Jorioz, Duingt, Doussard : ces communes lacustres abritent des propriétés souvent spacieuses, meublées avec goût, et occupées depuis longtemps. Les <Link to="/debarras-appartement-annecy" className="text-emerald-600 hover:text-emerald-700 font-medium">interventions dans les appartements du bassin annécien</Link> comme les vide-maisons y sont fréquents, notamment lors de successions. La bonne nouvelle : le potentiel de valorisation y est généralement élevé, ce qui tempère le coût.
                  </p>

                  <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6 rounded-r-lg">
                    <p className="text-red-900 font-medium mb-1">Erreur fréquente</p>
                    <p className="text-red-800 text-sm">
                      Ne comparez jamais un devis « par pièce » avec un devis « au m³ ». Ce sont deux logiques différentes. Une pièce quasi vide et une pièce encombrée du sol au plafond n'ont rien à voir en termes de travail. Exigez toujours une visite sur place avant d'accepter un tarif.
                    </p>
                  </div>

                  {/* H2 - Comment obtenir un devis fiable */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Comment obtenir un devis fiable pour votre débarras</h2>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">1. Privilégiez les entreprises qui se déplacent</h3>
                  <p className="mb-6">
                    Un devis sérieux nécessite une visite sur place. Tout professionnel qui vous donne un prix ferme par téléphone, sans avoir vu le bien, prend un risque — et vous aussi. Chez DEBECO, le déplacement pour le devis est gratuit et sans engagement sur tout le bassin annécien.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">2. Demandez un devis détaillé</h3>
                  <p className="mb-6">
                    Le devis doit mentionner clairement : le volume estimé, les pièces concernées, la main-d'œuvre, le transport, les frais de déchetterie, et la valorisation éventuelle des objets. Fuyez les devis en une ligne « forfait débarras maison : X € ».
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">3. Préparez le terrain si possible</h3>
                  <p className="mb-4">
                    Vous pouvez réduire le coût en faisant une partie du travail en amont :
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Triez les objets personnels que vous souhaitez conserver (photos, documents, bijoux)</li>
                    <li>Videz les tiroirs et placards — même si c'est en vrac dans des sacs</li>
                    <li>Dégagez les accès (couloirs, escaliers, entrée)</li>
                    <li>Signalez les objets lourds ou à traitement spécial (piano, coffre-fort, produits chimiques)</li>
                  </ul>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">4. Comparez, mais comparez le comparable</h3>
                  <p className="mb-6">
                    Il est sain de demander 2 ou 3 devis. Mais assurez-vous de comparer des prestations équivalentes : le devis inclut-il le tri sur place ? Le recyclage ? Le nettoyage après intervention ? Les frais de déchetterie ? Le moins-disant n'est pas toujours le mieux-disant — surtout si vous retrouvez vos meubles déposés au bord d'une route de campagne.
                  </p>

                </div>

                {/* FAQ */}
                <div className="mt-12 border-t-2 pt-10">
                  <div className="flex items-center gap-3 mb-8">
                    <HelpCircle className="w-7 h-7 text-emerald-600" />
                    <h2 className="text-2xl font-bold text-blue-900">Questions fréquentes sur les prix</h2>
                  </div>
                  <div className="space-y-4">
                    {faq.map((item, i) => (
                      <div key={i} className="p-5 bg-gray-50 rounded-xl border">
                        <h3 className="font-bold text-blue-900 mb-2">{item.question}</h3>
                        <p className="text-gray-600 leading-relaxed text-sm">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pour aller plus loin */}
                <div className="mt-12 border-t-2 pt-10">
                  <h2 className="text-2xl font-bold text-blue-900 mb-6">Pour aller plus loin</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Link to="/debarras-maison-annecy" className="group">
                      <div className="p-5 bg-blue-50 rounded-xl border-2 border-transparent hover:border-emerald-500 transition-all">
                        <h3 className="font-bold text-blue-900 mb-2 group-hover:text-emerald-700 transition-colors">Notre service de débarras de maison à Annecy</h3>
                        <p className="text-gray-600 text-sm">Découvrez notre méthode d'intervention quartier par quartier, de la Vieille Ville au Parmelan.</p>
                      </div>
                    </Link>
                    <Link to="/debarras-appartement-annecy" className="group">
                      <div className="p-5 bg-blue-50 rounded-xl border-2 border-transparent hover:border-emerald-500 transition-all">
                        <h3 className="font-bold text-blue-900 mb-2 group-hover:text-emerald-700 transition-colors">Débarras d'appartement à Annecy</h3>
                        <p className="text-gray-600 text-sm">Spécificités des immeubles annéciens : accès sans ascenseur, protection des parties communes, coordination syndic.</p>
                      </div>
                    </Link>
                    <Link to="/contact" className="group">
                      <div className="p-5 bg-emerald-50 rounded-xl border-2 border-transparent hover:border-emerald-500 transition-all">
                        <h3 className="font-bold text-blue-900 mb-2 group-hover:text-emerald-700 transition-colors">Demander un devis gratuit</h3>
                        <p className="text-gray-600 text-sm">Visite sur place, devis détaillé et sans engagement. Réponse sous 24 heures.</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Share */}
                <div className="mt-10 pt-6 border-t flex items-center gap-4 flex-wrap">
                  <span className="text-gray-500 text-sm font-medium flex items-center gap-2"><Share2 className="w-4 h-4" /> Partager :</span>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=https://debeco.fr/blog/${SLUG}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors" aria-label="Partager sur Facebook">
                    <Facebook className="w-5 h-5 text-blue-700" />
                  </a>
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=https://debeco.fr/blog/${SLUG}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors" aria-label="Partager sur LinkedIn">
                    <Linkedin className="w-5 h-5 text-blue-700" />
                  </a>
                  <button onClick={copyLink} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm text-gray-600" aria-label="Copier le lien">
                    <LinkIcon className="w-5 h-5" /> Copier le lien
                  </button>
                </div>

                {/* CTA */}
                <div className="mt-10 p-8 bg-gradient-to-br from-blue-900 to-emerald-800 rounded-2xl text-white text-center">
                  <h3 className="text-2xl font-bold mb-3">Besoin d'un débarras à Annecy ?</h3>
                  <p className="text-blue-100 mb-6">Devis gratuit en 24h. Intervention rapide dans tout le bassin annécien.</p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/contact">
                      <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl w-full sm:w-auto" data-testid="cta-devis">
                        Demander un devis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                    <a href="tel:0451401617">
                      <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 w-full sm:w-auto">
                        <Phone className="mr-2 w-5 h-5" />
                        04 51 40 16 17
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPrixDebarras;
